import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames/bind';
import styles from './Details.module.css';

import { Group, GroupMember, Student } from '../../../types/classroom';

import { useAppDispatch } from '../../../hooks/useRedux';

import {
  useUpdateGroupNameMutation,
  useValidateGroupNameMutation,
} from '../../../services/group';

import Table from '../Table';

import {
  CLASS_CAPACITY_EXCEEDED_MESSAGE,
  EMAIL,
  GROUP,
  INCREASE_CLASS_CAPACITY,
  INFORMATION,
  CLASSROOM_INFO_FIELDS,
  NAME,
  NICKNAME,
  SETTINGS,
  STUDENT,
  STUDENT_MANAGEMENT_INFO,
  TEACHER,
} from '../../../constants';
import { groupListChanged } from '../../../features/classroomSlice';
import MultipleSelectBox from '../../../components/MultipleSelectBox';
import { isMobile, isSmallScreenMediaQuery } from '../../../utils/util';
import { useNavigate } from 'react-router-dom';

interface Details {
  name: string;
  info: Student | Group | GroupMember;
  isOpen: boolean;
}

interface List {
  [key: string]: Box[];
}

interface Box {
  title: string;
  list: string[];
}

interface Row {
  item: string;
}

export default function Details({ name, info, isOpen }: Details) {
  const cx = classNames.bind(styles);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [validateGroupName] = useValidateGroupNameMutation();
  const [updateGroupName] = useUpdateGroupNameMutation();

  const [groupName, setGroupName] = useState(info.nm);
  const [validatedMsg, setValidatedMsg] = useState('');
  const [isValidGroupName, setIsValidGroupName] = useState(false);

  const detailAreaRef = useRef<HTMLDivElement>(null);

  const details: List = {
    student: [
      {
        title: `${SETTINGS} ${INFORMATION}`,
        list: [
          'dispStudentStatus',
          'dispStatus',
          'dispRequestDate',
          'dispApprovalDate',
          'dispReleaseDate',
        ],
      },
      {
        title: `${STUDENT} ${INFORMATION}`,
        list: [
          'email',
          'nm',
          'alias',
          'regionTypeNm',
          'schoolTypeNm',
          'schoolNm',
          'gradeTypeNm',
          'departmentTypeNm',
        ],
      },
    ],
    group: [
      {
        title: `${SETTINGS} ${INFORMATION}`,
        list: ['dispRegDate', 'cnt', 'nm'],
      },
      {
        title: `${STUDENT} ${INFORMATION}`,
        list: ['studentList'],
      },
    ],
    teacher: [
      {
        title: `${SETTINGS} ${INFORMATION}`,
        list: [
          'dispTeacherStatus',
          'dispStatus',
          'dispRequestDate',
          'dispApprovalDate',
          'dispReleaseDate',
        ],
      },
      {
        title: `${TEACHER} ${INFORMATION}`,
        list: [
          'email',
          'nm',
          'alias',
          'schoolTypeNm',
          'schoolNm',
          'gradeTypeNm',
          'subjectTypeNm',
        ],
      },
    ],
  };

  const headerName = {
    nm: NAME,
    alias: NICKNAME,
    email: EMAIL,
    dispStatus: SETTINGS,
  };

  const updateList = () => {};

  const changeGroupName = ({ target }: React.FocusEvent<HTMLInputElement>) => {
    const { value } = target;
    setGroupName(value);
    if (value === info.nm) {
      setValidatedMsg('');
      setIsValidGroupName(true);
      return;
    }
    validate(value);
  };

  const validate = async (value: string) => {
    try {
      const validatedRes = await validateGroupName(value);
      const msg = (validatedRes as { data: string }).data;
      const isValid = msg === 'OK';

      setValidatedMsg(msg);
      setIsValidGroupName(isValid);
      isValid && updateGroup(value);
    } catch (error) {
      console.error('Failed to update group name', error);
    }
  };

  const updateGroup = async (value: string) => {
    try {
      const updatedGroup = await updateGroupName({ seq: info.seq, nm: value });
      dispatch(
        groupListChanged({
          entity: (updatedGroup as { data: Group }).data,
          seq: info.seq,
        })
      );
    } catch (error) {
      console.error('Failed to update group name', error);
    }
  };

  function Box({ title, list }: Box) {
    return (
      <div className={cx('box')}>
        <p className={cx('tit')}>{title}</p>
        <div className={cx('list')}>
          {list.map((item) =>
            item === 'studentList' ? (
              <Table
                key={item}
                name="groupMember"
                headerList={['nm', 'alias', 'email', 'dispStatus']}
                headerName={headerName}
                list={info[item]}
                updateList={updateList}
                parentSeq={info.seq}
              />
            ) : item.includes('Date') && info[item] === null ? null : (
              <Row key={item} item={item} />
            )
          )}
        </div>
        {name === 'student' &&
          info.status === 5 &&
          title === STUDENT_MANAGEMENT_INFO && (
            <div className={cx('notice_over')}>
              <p className={cx('desc')}>
                <span
                  dangerouslySetInnerHTML={{
                    __html: CLASS_CAPACITY_EXCEEDED_MESSAGE,
                  }}
                ></span>
              </p>
              <div className={cx('btn_wrap')}>
                <button
                  type="button"
                  className={cx('btn', ' skyt', 'add_class_student')}
                  onClick={() => navigate('/home/settings/membership')}
                >
                  <span>{INCREASE_CLASS_CAPACITY}</span>
                </button>
              </div>
            </div>
          )}
      </div>
    );
  }

  function Row({ item }: Row) {
    return (
      <p>
        <span>{CLASSROOM_INFO_FIELDS[name][item]}</span>
        {name === 'group' && item === 'nm' ? (
          <span className={cx('detail')}>
            <span
              className={cx('input_box', {
                'input--error': !isValidGroupName,
              })}
            >
              <input
                type="text"
                className={cx('input_t')}
                defaultValue={groupName}
                onBlurCapture={changeGroupName}
              />
              {!isValidGroupName && (
                <span className={cx('msg')}>{validatedMsg}</span>
              )}
            </span>
          </span>
        ) : (
          <span className={cx({ status_info: item === 'dispStatus' })}>
            {info[item]
              ? item === 'cnt'
                ? `${info[item]}/${info.maxCnt}`
                : info[item]
              : '-'}
          </span>
        )}
      </p>
    );
  }

  useEffect(() => {
    if (isOpen && detailAreaRef.current) {
      detailAreaRef.current?.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }, []);

  return (
    <div className={cx('detail_area')} ref={detailAreaRef}>
      {details[name].map(({ title, list }) => (
        <Box key={title} title={title} list={list} />
      ))}
      {isMobile && name === 'student' && isSmallScreenMediaQuery && (
        <div className={cx('box')}>
          <p className={cx('tit')}>{GROUP}</p>
          <MultipleSelectBox item={info} list={info.groupList} />
        </div>
      )}
    </div>
  );
}
